// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a ready card (design/suggestion-stories a-cooked-followup-r2, a-cooked-prep-r2, b-feed-r3,
// p-feed-r2): what Konvo already did, in one renderer for the corner pop-up ('popup', with a tail
// at the icon), the phone card above the bar ('phone') and an entry in the From Konvo list
// ('entry': icon, headline, one detail and the time; expanded it also shows the written reply).
// The pop-up shows the context line, up to three facts with icon tiles (ok/warn tone), the reply
// Konvo wrote, the buttons (the first is the main one) and a quiet note.

import cardStyles from '@app/features/konvo_corner/components/CornerCard.module.css';
import styles from '@app/features/konvo_corner/components/ReadyCard.module.css';
import {entryDetail} from '@app/features/konvo_corner/state/ReadyListModel';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {
	CalendarBlankIcon,
	CalendarPlusIcon,
	ChatCircleIcon,
	CheckCircleIcon,
	CheckIcon,
	CheckSquareIcon,
	ClockIcon,
	DotsThreeIcon,
	EnvelopeSimpleIcon,
	FileTextIcon,
	InfoIcon,
	PaperPlaneRightIcon,
	PencilSimpleIcon,
	RobotIcon,
	SparkleIcon,
	UserIcon,
	VideoCameraIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import type {
	ReadyAction,
	ReadyActionKind,
	ReadyCard as ReadyCardData,
	ReadyItem,
	ReadyLine,
	ReadyLineIcon,
} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';

const CONTEXT = msg({
	message: 'Konvo · {context}',
	comment:
		'Top line of a ready card from Konvo. context says who or when, e.g. "Danie Coetzee, iCAM" or "in 20 minutes".',
});
const NOW = msg({message: 'Konvo · now', comment: 'Top line of a corner assistant pop-up.'});
const REPLY_READY = msg({message: 'Reply ready', comment: 'Ready card: heading above the reply Konvo already wrote.'});
const REPLY_TO = msg({
	message: 'To {name}',
	comment: 'Ready card: who the reply Konvo wrote goes to. name is a person.',
});
const MORE = msg({message: 'More options', comment: 'Menu button on a corner assistant pop-up.'});
const CLOSE = msg({message: 'Close', comment: 'Close button on a corner assistant pop-up.'});
const REMOVE = msg({
	message: 'Remove from the list',
	comment: 'From Konvo list: the ✕ on an entry (dismisses it).',
});
const REGION = msg({message: 'Suggestion from Konvo', comment: 'Accessible name of a corner assistant pop-up.'});
const DONE_LINE = msg({
	message: '{note}: {title}',
	comment: 'Done today entry in the From Konvo list. note is what happened ("Sent", "Done"), title is the entry.',
});

export const LINE_ICON: Record<ReadyLineIcon, (size: number) => React.ReactNode> = {
	mail: (size) => <EnvelopeSimpleIcon size={size} />,
	calendar: (size) => <CalendarBlankIcon size={size} />,
	task: (size) => <CheckSquareIcon size={size} />,
	promise: (size) => <CheckSquareIcon size={size} />,
	file: (size) => <FileTextIcon size={size} />,
	person: (size) => <UserIcon size={size} />,
	time: (size) => <ClockIcon size={size} />,
	agent: (size) => <RobotIcon size={size} />,
	chat: (size) => <ChatCircleIcon size={size} />,
};

const ACTION_ICON: Partial<Record<ReadyActionKind, (size: number) => React.ReactNode>> = {
	send_reply: (size) => <PaperPlaneRightIcon size={size} weight="bold" />,
	open_mail: (size) => <PencilSimpleIcon size={size} weight="bold" />,
	add_event: (size) => <CalendarPlusIcon size={size} weight="bold" />,
	mail_task: (size) => <CheckSquareIcon size={size} weight="bold" />,
	promise_done: (size) => <CheckIcon size={size} weight="bold" />,
	open_brief: (size) => <SparkleIcon size={size} weight="bold" />,
	join: (size) => <VideoCameraIcon size={size} weight="bold" />,
	open_dm: (size) => <ChatCircleIcon size={size} weight="bold" />,
	run: (size) => <SparkleIcon size={size} weight="bold" />,
};

/** The icon of a list entry: what the card is mostly about. */
function entryIcon(item: ReadyItem, size: number): React.ReactNode {
	if (item.card.reply) return LINE_ICON.mail(size);
	switch (item.kind) {
		case 'meeting_prep':
		case 'meeting_soon':
			return LINE_ICON.calendar(size);
		case 'promise_due':
		case 'commitment_due':
			return LINE_ICON.promise(size);
		case 'client_followup':
		case 'vip_mail':
			return LINE_ICON.mail(size);
		case 'review_time':
			return LINE_ICON.time(size);
		case 'agent_task':
			return LINE_ICON.agent(size);
	}
	const line = item.card.lines[0];
	return line ? LINE_ICON[line.icon](size) : <SparkleIcon size={size} />;
}

/** The detail of a fact; with a tone, its first part ("You are free · …") carries the colour. */
function LineDetail({line}: {line: ReadyLine}) {
	if (!line.detail) return null;
	const toneClass = line.tone === 'ok' ? styles.ok : line.tone === 'warn' ? styles.warn : null;
	if (!toneClass) return <span className={styles.lineDetail}>{line.detail}</span>;
	const split = line.detail.indexOf(' · ');
	if (split < 0)
		return (
			<span className={styles.lineDetail}>
				<span className={toneClass}>{line.detail}</span>
			</span>
		);
	return (
		<span className={styles.lineDetail}>
			<span className={toneClass}>{line.detail.slice(0, split)}</span>
			{line.detail.slice(split)}
		</span>
	);
}

function Lines({lines}: {lines: ReadonlyArray<ReadyLine>}) {
	if (lines.length === 0) return null;
	return (
		<ul className={styles.lines} data-flx="konvo-corner.ready.lines">
			{lines.map((line, index) => (
				<li key={index} className={styles.line} data-tone={line.tone}>
					<span className={styles.tile}>{LINE_ICON[line.icon](16)}</span>
					<span className={styles.lineText}>
						<span className={styles.lineTitle}>{line.title}</span>
						<LineDetail line={line} />
					</span>
				</li>
			))}
		</ul>
	);
}

function ReplyBox({card, clamp}: {card: ReadyCardData; clamp: boolean}) {
	const {i18n} = useLingui();
	const reply = card.reply;
	if (!reply) return null;
	return (
		<div className={styles.reply} data-flx="konvo-corner.ready.reply">
			{!clamp && (
				<div className={styles.replyHead}>
					<PencilSimpleIcon size={14} />
					<span className={styles.replyLabel}>{i18n._(REPLY_READY)}</span>
					{reply.to_name && <span className={styles.replyTo}>{i18n._(REPLY_TO, {name: reply.to_name})}</span>}
				</div>
			)}
			<p className={clsx(styles.replyBody, clamp && styles.replyClamp)}>{reply.body}</p>
		</div>
	);
}

function Actions({
	actions,
	variant,
	onAction,
}: {
	actions: ReadonlyArray<ReadyAction>;
	variant: 'popup' | 'phone' | 'entry';
	onAction: (action: ReadyAction, event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
	const entry = variant === 'entry';
	// A list entry keeps to its main button and one more (b-feed-r3); the pop-up shows all three.
	const buttons = (entry ? actions.slice(0, 2) : actions).map((action, index) => {
		const primary = index === 0;
		const icon = primary && !entry ? ACTION_ICON[action.kind]?.(16) : null;
		return (
			<button
				key={index}
				type="button"
				className={
					entry
						? clsx(styles.small, primary ? styles.smallPrimary : styles.smallSecondary)
						: primary
							? cardStyles.primary
							: cardStyles.secondary
				}
				onClick={(event) => onAction(action, event)}
				data-flx={primary ? 'konvo-corner.ready.main' : 'konvo-corner.ready.action'}
				data-kind={action.kind}
			>
				{icon}
				<span>{action.label}</span>
			</button>
		);
	});
	// In the list the main button sits at the right end (b-feed-r3).
	return (
		<div className={clsx(cardStyles.actions, styles.actions, entry && styles.entryActions)}>
			{entry ? [...buttons.slice(1), buttons[0]] : buttons}
		</div>
	);
}

interface PopupProps {
	card: ReadyCardData;
	variant: 'popup' | 'phone';
	kind: string;
	onAction: (action: ReadyAction, event: React.MouseEvent<HTMLButtonElement>) => void;
	onClose: () => void;
	onMenu?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	onHover?: (entering: boolean) => void;
	className?: string;
	/** The phone's menu sheet, kept inside the card. */
	children?: React.ReactNode;
}

/** The pop-up (desktop corner or phone, above the bar). */
export function ReadyPopupCard({
	card,
	variant,
	kind,
	onAction,
	onClose,
	onMenu,
	onHover,
	className,
	children,
}: PopupProps) {
	const {i18n} = useLingui();
	return (
		<section
			className={clsx(
				cardStyles.card,
				variant === 'phone' ? cardStyles.phone : cardStyles.desktop,
				styles.card,
				variant === 'popup' && styles.popup,
				className,
			)}
			aria-label={i18n._(REGION)}
			aria-keyshortcuts="Alt+Shift+K"
			tabIndex={-1}
			onMouseEnter={onHover ? () => onHover(true) : undefined}
			onMouseLeave={onHover ? () => onHover(false) : undefined}
			data-kind={kind}
			data-ready="true"
			data-flx="konvo-corner.card"
		>
			<div className={cardStyles.top}>
				<KonvoLogo size={18} />
				<span className={cardStyles.context} data-flx="konvo-corner.ready.context">
					{card.context ? i18n._(CONTEXT, {context: card.context}) : i18n._(NOW)}
				</span>
				{onMenu && (
					<button
						type="button"
						className={cardStyles.iconButton}
						aria-label={i18n._(MORE)}
						onClick={onMenu}
						data-flx="konvo-corner.card.menu"
					>
						<DotsThreeIcon size={18} weight="bold" />
					</button>
				)}
				<button
					type="button"
					className={cardStyles.iconButton}
					aria-label={i18n._(CLOSE)}
					onClick={onClose}
					data-flx="konvo-corner.card.close"
				>
					<XIcon size={16} />
				</button>
			</div>
			<p className={cardStyles.title} data-flx="konvo-corner.card.title">
				{card.headline}
			</p>
			<Lines lines={card.lines} />
			<ReplyBox card={card} clamp={false} />
			<Actions actions={card.actions} variant={variant} onAction={onAction} />
			{card.note && (
				<p className={styles.note} data-flx="konvo-corner.card.reason">
					<InfoIcon size={14} className={styles.noteIcon} />
					<span>{card.note}</span>
				</p>
			)}
			{children}
		</section>
	);
}

interface EntryProps {
	item: ReadyItem;
	expanded: boolean;
	time: string | null;
	onAction: (action: ReadyAction, event: React.MouseEvent<HTMLButtonElement>) => void;
	onDismiss: () => void;
}

/** One entry in Ready for you: compact, the first one open with Konvo's reply. */
export function ReadyEntry({item, expanded, time, onAction, onDismiss}: EntryProps) {
	const {i18n} = useLingui();
	const card = item.card;
	const detail = entryDetail(item);
	return (
		<article
			className={clsx(styles.entry, expanded && styles.entryOpen)}
			data-flx="konvo-corner.ready.entry"
			data-id={item.id}
			data-kind={item.kind}
			data-open={expanded || undefined}
		>
			<div className={styles.entryRow}>
				<span className={styles.tile}>{entryIcon(item, 16)}</span>
				<span className={styles.entryMain}>
					<span className={styles.entryTitle} data-flx="konvo-corner.ready.entry.title">
						{card.headline}
					</span>
					{detail && <span className={styles.entryDetail}>{detail}</span>}
				</span>
				{time && <span className={styles.entryTime}>{time}</span>}
				<button
					type="button"
					className={clsx(cardStyles.iconButton, styles.entryDismiss)}
					aria-label={i18n._(REMOVE)}
					onClick={onDismiss}
					data-flx="konvo-corner.ready.entry.dismiss"
				>
					<XIcon size={14} />
				</button>
			</div>
			{expanded && <ReplyBox card={card} clamp />}
			<Actions actions={card.actions} variant="entry" onAction={onAction} />
		</article>
	);
}

/** One line under Done today. */
export function ReadyDoneEntry({item, time}: {item: ReadyItem; time: string | null}) {
	const {i18n} = useLingui();
	const title = item.done_note
		? i18n._(DONE_LINE, {note: item.done_note, title: item.card.headline})
		: item.card.headline;
	return (
		<div className={clsx(styles.entry, styles.entryDone)} data-flx="konvo-corner.ready.done" data-id={item.id}>
			<div className={styles.entryRow}>
				<span className={styles.tile}>
					{item.card.reply ? <PaperPlaneRightIcon size={16} /> : <CheckCircleIcon size={16} />}
				</span>
				<span className={styles.entryMain}>
					<span className={styles.entryTitle}>{title}</span>
				</span>
				{time && <span className={styles.entryTime}>{time}</span>}
			</div>
		</div>
	);
}
